import { createTheme, Theme } from "@mui/material/styles";

const theme = createTheme({
  cssVariables: true,
  palette: {
    mode: "light",
    primary: {
      main: "#22428f",
      light: "#4a6bb8",
      dark: "#1a336f",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#ABA599",
      light: "#c4bdb3",
      dark: "#8a8477",
      contrastText: "#ffffff",
    },
    background: {
      default: "#f5f5f7",
      paper: "#ffffff",
    },
    text: {
      primary: "#1d1d1f",
      secondary: "#6e6e73",
    },
    divider: "#d2d2d7",
    error: {
      main: "#d32f2f",
    },
    warning: {
      main: "#ed6c02",
    },
    info: {
      main: "#0288d1",
    },
    success: {
      main: "#2e7d32",
    },
  },
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
    ].join(","),
    h1: {
      fontSize: "2.5rem",
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: "-0.01em",
    },
    h3: {
      fontSize: "1.75rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: "1.125rem",
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.6,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.6,
    },
    button: {
      textTransform: "none",
      fontWeight: 600,
      letterSpacing: "0.01em",
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    "none",
    "0px 2px 4px rgba(0, 0, 0, 0.04)",
    "0px 4px 8px rgba(0, 0, 0, 0.06)",
    "0px 8px 16px rgba(0, 0, 0, 0.08)",
    "0px 12px 24px rgba(0, 0, 0, 0.1)",
    "0px 16px 32px rgba(0, 0, 0, 0.12)",
    "0px 20px 40px rgba(0, 0, 0, 0.14)",
    "0px 24px 48px rgba(0, 0, 0, 0.16)",
    "0px 28px 56px rgba(0, 0, 0, 0.18)",
    "0px 32px 64px rgba(0, 0, 0, 0.2)",
    "0px 36px 72px rgba(0, 0, 0, 0.22)",
    "0px 40px 80px rgba(0, 0, 0, 0.24)",
    "0px 44px 88px rgba(0, 0, 0, 0.26)",
    "0px 48px 96px rgba(0, 0, 0, 0.28)",
    "0px 52px 104px rgba(0, 0, 0, 0.3)",
    "0px 56px 112px rgba(0, 0, 0, 0.32)",
    "0px 60px 120px rgba(0, 0, 0, 0.34)",
    "0px 64px 128px rgba(0, 0, 0, 0.36)",
    "0px 68px 136px rgba(0, 0, 0, 0.38)",
    "0px 72px 144px rgba(0, 0, 0, 0.4)",
    "0px 76px 152px rgba(0, 0, 0, 0.42)",
    "0px 80px 160px rgba(0, 0, 0, 0.44)",
    "0px 84px 168px rgba(0, 0, 0, 0.46)",
    "0px 88px 176px rgba(0, 0, 0, 0.48)",
    "0px 92px 184px rgba(0, 0, 0, 0.5)",
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "10px 24px",
          fontSize: "0.9375rem",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
        contained: {
          "&:hover": {
            boxShadow: (theme: Theme) =>
              `0px 4px 12px ${theme.palette.primary.main}4D`,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
        },
        elevation1: {
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
        },
        elevation2: {
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
        },
        elevation3: {
          boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.12)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
            transform: "translateY(-2px)",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            "&:not(.MuiInputBase-multiline)": {
              "&:not(.MuiInputBase-sizeSmall)": {
                height: "46px",
                "& .MuiOutlinedInput-input": {
                  padding: "10px 16px",
                  "&:-webkit-autofill": {
                    backgroundColor: "transparent !important",
                    boxShadow: "none !important",
                    WebkitTextFillColor: "#1d1d1f !important",
                    WebkitBoxShadow:
                      "0 0 0 1000px transparent inset !important",
                    transition:
                      "background-color 5000s ease-in-out 0s !important",
                  },
                },
              },
              "&.MuiInputBase-sizeSmall": {
                height: "46px",
                "& .MuiOutlinedInput-input": {
                  padding: "10px 16px",
                },
              },
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "primary.main",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "primary.main",
              borderWidth: 2,
            },
          },
          "& .MuiInputLabel-root": {
            "&:not(.MuiInputLabel-sizeSmall)": {
              transform: "translate(16px, 12px) scale(1)",
              "&.MuiInputLabel-shrink": {
                transform: "translate(16px, -9px) scale(0.75)",
              },
            },
            "&.MuiInputLabel-sizeSmall": {
              transform: "translate(16px, 12px) scale(1)",
              "&.MuiInputLabel-shrink": {
                transform: "translate(16px, -9px) scale(0.75)",
              },
            },
          },
          "& .MuiInputAdornment-root": {
            "&.MuiInputAdornment-positionStart": {
              marginRight: "8px",
            },
            "&.MuiInputAdornment-positionEnd": {
              marginLeft: "8px",
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: "0.75rem",
          height: 24,
        },
        sizeSmall: {
          fontSize: "0.75rem",
          height: 24,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        },
        root: {
          "&.MuiDialog-root": {
            "& .MuiBackdrop-root": {
              transition: "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            },
          },
        },
      },
      defaultProps: {
        TransitionProps: {
          timeout: 300,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          paddingBottom: 16,
          borderBottom: "1px solid",
          borderColor: "divider",
          fontWeight: 600,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 24,
          gap: 8,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: (theme: Theme) =>
              `${theme.palette.primary.main}14`,
          },
        },
        colorPrimary: {
          color: "primary.main",
          "&:hover": {
            backgroundColor: (theme: Theme) =>
              `${theme.palette.primary.main}14`,
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.2)",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 24,
          "&:last-child": {
            paddingBottom: 24,
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 16,
          paddingRight: 16,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "divider",
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 8,
          "&:before": {
            display: "none",
          },
          "&.Mui-expanded": {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          "&.Mui-expanded": {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid",
          borderColor: "divider",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.9375rem",
          minHeight: 48,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "rgba(0, 0, 0, 0.87)",
          fontSize: "0.75rem",
          padding: "6px 12px",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          backgroundColor: "transparent",
          color: "text.primary",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: "64px !important",
          paddingLeft: "16px",
          paddingRight: "16px",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: "0px 12px 24px rgba(0, 0, 0, 0.1)",
          marginTop: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          margin: "4px 8px",
          "&:hover": {
            backgroundColor: (theme: Theme) =>
              `${theme.palette.primary.main}14`,
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 40,
          justifyContent: "center",
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize: "0.625rem",
          height: 18,
          minWidth: 18,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: "1px solid",
          borderRightColor: "divider",
          boxShadow: "none",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: "4px 8px",
          "&.Mui-selected": {
            backgroundColor: (theme: Theme) =>
              `${theme.palette.primary.main}14`,
            color: "primary.main",
            "&:hover": {
              backgroundColor: (theme: Theme) =>
                `${theme.palette.primary.main}1F`,
            },
            "& .MuiListItemIcon-root": {
              color: "primary.main",
            },
          },
          "&:hover": {
            backgroundColor: (theme: Theme) =>
              `${theme.palette.primary.main}0A`,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        "*": {
          boxSizing: "border-box",
        },
        html: {
          height: "100%",
          scrollBehavior: "smooth",
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
        body: {
          margin: 0,
          padding: 0,
          minHeight: "100vh",
          overflowX: "hidden",
        },
        "#root": {
          minHeight: "100vh",
          width: "100%",
        },
        "h1, h2, h3, h4, h5, h6": {
          margin: 0,
        },
        p: {
          margin: 0,
        },
        a: {
          textDecoration: "none",
          transition: "color 0.2s ease-in-out",
          "&:hover": {
            color: "primary.dark",
          },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: "2px",
            borderRadius: "4px",
          },
        },
        "::-webkit-scrollbar": {
          width: "10px",
          height: "10px",
        },
        "::-webkit-scrollbar-track": {
          background: "background.default",
        },
        "::-webkit-scrollbar-thumb": {
          background: "divider",
          borderRadius: "5px",
          "&:hover": {
            background: "text.secondary",
          },
        },
        "::selection": {
          backgroundColor: (theme: Theme) => `${theme.palette.primary.main}33`,
        },
        "*:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: "2px",
        },
      },
    },
  },
});

export default theme;
